import { keyBy } from "lodash";
import { normalize, schema } from "normalizr";
import { Entities } from "../../normalizr/entities";
import { doReconstructEntities, doUpdateEntities } from "../Middleware/apiMiddleware";
import { deleteEntities, deleteEntity } from "./actions";
import { createEntityReducer, EntityReducerParams } from "./reducers";

interface UpdateOptions {
    normalize?: boolean;
    overrideAll?: boolean;
}

/**
 * A function that accepts an entity schema and generates entities reducer, based on schema key,
 * and the next action creators: "updateEntities" and "deleteEntity".
 * The main goal of usage the slice is to encapsulate the entity schema
 * and use appropriate actions for the generated reducer slice.
 *
 * @param entitySchema
 */
export const createEntitiesSlice = <EntityType>(entitySchema: schema.Entity, params?: EntityReducerParams) => {
    const entitiesSchema = new schema.Array(entitySchema);
    const reducer = createEntityReducer<Entities<EntityType>>(entitySchema.key, params);

    /**
     * An action creator for entities update.
     * It is bound to the entity schema key, so you're confident the action will reach the necessary destination.
     *
     * @param items
     * @param options
     */
    const updateEntities = (items: Partial<EntityType>[], options?: UpdateOptions) => {
        const entities = options?.normalize
            ? normalize(items, entitiesSchema).entities
            : {
                  // pass context to entitySchema.getId is obligatory
                  [entitySchema.key]: keyBy(items, item => entitySchema.getId.call(entitySchema, item)),
              };

        return options?.overrideAll
            ? doReconstructEntities(entities, entitySchema.key)
            : doUpdateEntities(entities, entitySchema.key);
    };

    /**
     * An action creator for an entity deletion.
     * It is bound to the entity schema key, so you're confident the action will reach the necessary destination.
     *
     * @param key
     */
    const deleteEntityByKey = (key: string | number) => {
        return deleteEntity(key, entitySchema.key);
    };

    /**
     * An action creator for an entities deletion.
     * It is bound to the entity schema key, so you're confident the action will reach the necessary destination.
     *
     * @param keys
     */
    const deleteEntitiesByKeys = (keys: number[]) => {
        return deleteEntities(keys, entitySchema.key);
    };

    /**
     * An action creator for removing all entities from store.
     */
    const deleteAllEntities = () => {
        return doReconstructEntities({}, entitySchema.key);
    };

    return {
        reducer,
        updateEntities,
        deleteEntity: deleteEntityByKey,
        deleteEntities: deleteEntitiesByKeys,
        deleteAllEntities,
    };
};
